import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Users as UsersIcon, KeyRound, UserX, UserCheck, Search, RefreshCw, ShieldCheck, Lock, Unlock, LayoutGrid, Pencil } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Table, type Column } from '@/shared/components/ui/Table';
import { roleLabel } from '@/shared/permissions/roles';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import { consoleAuthorityOf } from '@/shared/permissions/administration';
import { tierOf } from '@/shared/permissions/identity';
import { useAdminUsers, useUserMutations, useLeaves } from '../hooks/useHr';
import { HrStatCards } from '../components/HrStatCards';
import { CreateUserModal } from '../components/CreateUserModal';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import { UserPermissionsModal } from '../components/UserPermissionsModal';
import { UserAccessModal } from '../components/UserAccessModal';
import { DEPT_LABELS, authorityLabel, authorityTone, fmtDate } from '../constants';
import type { HrUser, CreateUserPayload } from '../types';
import { ROUTES } from '@/routes/paths';
import { localToday } from '@/shared/utils/utils';

const DEPT_FILTER_OPTIONS = [
  { value: 'all', label: 'All Departments' },
  { value: 'sales', label: 'Sales' },
  { value: 'hr', label: 'HR' },
  { value: 'it', label: 'IT' },
  { value: 'digital_marketing', label: 'Digital Mktg' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'management', label: 'Management' },
];

const TIER_FILTER_OPTIONS = [
  { value: 'all', label: 'All Tiers' },
  { value: 'SUPER_ADMIN', label: authorityLabel('superadmin') },
  { value: 'ADMIN', label: authorityLabel('admin') },
  { value: 'EMPLOYEE', label: authorityLabel('employee') },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

export function HrPage() {
  const navigate = useNavigate();
  const user = useUser();
  const authority = consoleAuthorityOf(user);
  const isSuperAdmin = authority.showExecutiveControls;
  const myDept = user?.department ?? 'sales';

  const { data: users = [], isLoading, isError, isFetching, refetch } = useAdminUsers();
  const { data: leaves = [] } = useLeaves();
  const { createUser, updateUser, unlockUser } = useUserMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<HrUser | null>(null);
  // Per-user permissions (client, 2026-10-06): give or take away ACTIONS for
  // ONE person on top of their role.
  const [permTarget, setPermTarget] = useState<HrUser | null>(null);
  // Which SCREENS / workspace modules a person may open at all. Same editor that
  // user creation already uses, wired to the existing-user path so an account
  // created last month is not stuck with whatever it was born with.
  const [accessTarget, setAccessTarget] = useState<HrUser | null>(null);
  const [disableTarget, setDisableTarget] = useState<HrUser | null>(null);

  // Toolbar filter state
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // ─── HR stat cards (mirror legacy loadHrHub) ───────────────────────────────
  const stats = useMemo(() => {
    const today = localToday();
    const staff = users.filter((u) => u.role !== 'superadmin');
    const onLeave = leaves.filter(
      (l) => l.status === 'approved' && l.from_date <= today && l.to_date >= today,
    ).length;
    return {
      totalStaff: staff.length,
      pendingLeaves: leaves.filter((l) => l.status === 'pending').length,
      onLeave,
      activeStaff: Math.max(0, staff.filter((u) => u.active).length - onLeave),
    };
  }, [users, leaves]);

  // Executive-only authority-tier distribution (derived from real data)
  const tierDist = useMemo(() => {
    let exec = 0;
    let admin = 0;
    let employee = 0;
    for (const u of users) {
      const t = tierOf(u.role);
      if (t === 'SUPER_ADMIN') exec++;
      else if (t === 'ADMIN') admin++;
      else employee++;
    }
    return { exec, admin, employee };
  }, [users]);

  // Presentation-only mirror of server authorization. Server guards remain truth.
  const canModify = (u: HrUser) => {
    if (authority.tier === 'SUPER_ADMIN') return true;
    if (authority.tier !== 'ADMIN' || u.department !== myDept) return false;
    return u.id === user?.id || tierOf(u.role) === 'EMPLOYEE';
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (deptFilter !== 'all' && u.department !== deptFilter) return false;
      if (tierFilter !== 'all' && tierOf(u.role) !== tierFilter) return false;
      if (statusFilter === 'active' && !u.active) return false;
      if (statusFilter === 'disabled' && u.active) return false;
      if (q) {
        const hay = [u.display_name, u.username, DEPT_LABELS[u.department ?? ''] ?? u.department, authorityLabel(u.role)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, deptFilter, tierFilter, statusFilter]);

  const handleCreate = (payload: CreateUserPayload) => {
    createUser.mutate(payload, {
      onSuccess: () => {
        toast.success('User created.');
        setCreateOpen(false);
      },
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to create user.')),
    });
  };

  const handleResetPassword = (password: string, currentPassword: string) => {
    if (!resetTarget) return;
    updateUser.mutate(
      { id: resetTarget.id, payload: { password, currentPassword } },
      {
        onSuccess: () => {
          toast.success('Password reset.');
          setResetTarget(null);
        },
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to reset password.')),
      },
    );
  };

  // Role promote/demote and department transfer now live on the full
  // Employee 360 page (2026-08-16) — row click navigates straight there
  // instead of opening a side drawer, so those actions belong there too
  // rather than being duplicated in two places.
  const handleToggleActive = (u: HrUser, makeActive: boolean) => {
    updateUser.mutate(
      { id: u.id, payload: { active: makeActive } },
      {
        onSuccess: () => {
          toast.success(makeActive ? 'Account enabled.' : 'Account disabled.');
          setDisableTarget(null);
        },
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to update account.')),
      },
    );
  };

  const handleRename = (u: HrUser) => {
    const displayName = window.prompt('Enter the employee name shown throughout CRM:', u.display_name)?.trim();
    if (!displayName || displayName === u.display_name) return;
    updateUser.mutate(
      { id: u.id, payload: { displayName } },
      {
        onSuccess: () => toast.success('User name updated.'),
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to update user name.')),
      },
    );
  };

  // A future locked_until means the login route itself is refusing this
  // account (3 failed attempts) regardless of what active/disabled says.
  const isLocked = (u: HrUser) => !!u.locked_until && new Date(u.locked_until).getTime() > Date.now();

  const handleUnlock = (u: HrUser) => {
    unlockUser.mutate(u.id, {
      onSuccess: () => toast.success(`${u.display_name}'s account unlocked — they can log in again.`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to unlock account.')),
    });
  };

  // Screen (workspace module) grants for an existing user. The server answers
  // 403 with the specific modules a caller may not give, so that text is shown
  // rather than swallowed — a silently-partial save is how this feature looked
  // broken for administrators in the first place.
  const handleAccessSave = async (permissions: string[]) => {
    if (!accessTarget) return;
    try {
      await updateUser.mutateAsync({ id: accessTarget.id, payload: { permissions } });
      toast.success(`${accessTarget.display_name}'s screen access updated`);
      setAccessTarget(null);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message ?? 'Could not save screen access.');
    }
  };

  const renderRowActions = (u: HrUser) => {
    if (!canModify(u)) return <span className="text-[11px] text-on-surface-variant/50">—</span>;
    const isSelf = u.id === user?.id;
    return (
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleRename(u); }}>
          <Pencil className="h-3.5 w-3.5" /> Rename
        </Button>
        {isLocked(u) && (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleUnlock(u); }} loading={unlockUser.isPending}>
            <Unlock className="h-3.5 w-3.5" /> Unlock
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setResetTarget(u); }}>
          <KeyRound className="h-3.5 w-3.5" /> Reset
        </Button>
        {/*
          Both axes of access, for anyone this caller may modify. They were
          hidden behind isSuperAdmin, so a department admin saw neither — even
          though the server now lets an administrator delegate within their own
          department, up to what they hold themselves. Self is excluded: the
          server refuses editing your own access, and offering it would only
          produce an error.
        */}
        {canModify(u) && !isSelf && u.role !== 'superadmin' && (
          <>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setAccessTarget(u); }}>
              <LayoutGrid className="h-3.5 w-3.5" /> Screens
            </Button>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setPermTarget(u); }}>
              <ShieldCheck className="h-3.5 w-3.5" /> Permissions
            </Button>
          </>
        )}
        {isSelf ? (
          <span className="text-[11px] text-on-surface-variant/50">(you)</span>
        ) : u.active ? (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setDisableTarget(u); }}>
            <UserX className="h-3.5 w-3.5" /> Disable
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleToggleActive(u, true); }}>
            <UserCheck className="h-3.5 w-3.5" /> Enable
          </Button>
        )}
      </div>
    );
  };

  const columns: Column<HrUser>[] = [
    {
      key: 'name',
      header: 'Employee',
      render: (u) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={u.display_name} size="sm" />
          <div className="min-w-0">
            <div className="font-medium text-on-surface">{u.display_name}</div>
            <div className="text-[11px] text-on-surface-variant">@{u.username}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Authority',
      render: (u) => (
        <div className="flex flex-col gap-0.5">
          <Badge tone={authorityTone(u.role)}>
            <ShieldCheck className="h-3 w-3" /> {authorityLabel(u.role)}
          </Badge>
          {/* The badge is the AUTHORITY tier, so a `bde` correctly reads as
              Employee there — true, but not what an administrator is scanning
              for. The role itself sits underneath so the two stop being
              mistaken for each other. */}
          <span className="text-[11px] text-on-surface-variant">{roleLabel(u.role)}</span>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (u) => <span className="text-on-surface-variant">{DEPT_LABELS[u.department ?? ''] ?? u.department ?? '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <div className="flex items-center gap-2">
          <Badge tone={u.active ? 'success' : 'danger'}>{u.active ? 'Active' : 'Disabled'}</Badge>
          {isLocked(u) && (
            <Badge
              tone="warning"
              // The lock window is minutes, not days — fmtDate's day-only
              // granularity (used for "Joined" etc.) would be misleading here.
              title={`${u.failed_login_attempts ?? 3} failed login attempts — locked until ${new Date(u.locked_until as string).toLocaleTimeString()}`}
            >
              <Lock className="h-3 w-3" /> Locked
            </Badge>
          )}
          {u.is_checked_in && <span className="text-[10px] text-success-text">● In</span>}
        </div>
      ),
    },
    { key: 'created', header: 'Joined', render: (u) => <span className="text-on-surface-variant">{fmtDate(u.created_at)}</span> },
    { key: 'leads', header: 'Leads', align: 'center', render: (u) => <span className="font-semibold tabular-nums">{u.lead_count ?? 0}</span> },
    { key: 'actions', header: 'Actions', align: 'right', render: renderRowActions },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={UsersIcon}
        eyebrow="Administration"
        title="Team &amp; Access"
        description={<>{isSuperAdmin ? 'Organization-wide team directory & access.' : "Manage your department's team & access."}</>}
        actions={
          <><Button onClick={() => setCreateOpen(true)} size="md">
                  <Plus className="h-4 w-4" /> Create User
                </Button></>
        }
      />

      <HrStatCards {...stats} />

      {/* Executive-only authority distribution (CEO view) */}
      {isSuperAdmin && (
        <div className="flex flex-wrap items-center gap-2 text-body-sm">
          <span className="text-on-surface-variant">Authority distribution:</span>
          <Badge tone="primary">{tierDist.exec} {authorityLabel('superadmin')}</Badge>
          <Badge tone="info">{tierDist.admin} {authorityLabel('admin')}</Badge>
          <Badge tone="neutral">{tierDist.employee} {authorityLabel('employee')}</Badge>
        </div>
      )}

      {/* Enterprise toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            name="user-search"
            placeholder="Search name, username, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        {isSuperAdmin && (
          <div className="w-[170px]">
            <Select name="dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} options={DEPT_FILTER_OPTIONS} />
          </div>
        )}
        <div className="w-[150px]">
          <Select name="tier-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} options={TIER_FILTER_OPTIONS} />
        </div>
        <div className="w-[150px]">
          <Select name="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={STATUS_FILTER_OPTIONS} />
        </div>
        <Button variant="outline" size="md" onClick={() => refetch()} loading={isFetching} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load users" description="Check your connection or try refreshing." />
      ) : (
        <Table
          columns={columns}
          data={filtered}
          rowKey={(u) => u.id}
          onRowClick={(u) => navigate(ROUTES.employee360.replace(':userId', encodeURIComponent(u.id)))}
          empty={users.length === 0 ? 'No users in your department yet.' : 'No users match your filters.'}
        />
      )}

      <CreateUserModal
        open={createOpen}
        department={myDept}
        isSuperAdmin={isSuperAdmin}
        loading={createUser.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <UserPermissionsModal
        user={permTarget}
        open={!!permTarget}
        onClose={() => setPermTarget(null)}
        onSaved={() => setPermTarget(null)}
      />

      <UserAccessModal
        user={accessTarget}
        open={!!accessTarget}
        loading={updateUser.isPending}
        onClose={() => setAccessTarget(null)}
        onSubmit={handleAccessSave}
      />

      <ResetPasswordModal
        open={!!resetTarget}
        userName={resetTarget?.display_name ?? ''}
        targetRole={resetTarget?.role ?? 'employee'}
        loading={updateUser.isPending}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
      />

      <ConfirmDialog
        open={!!disableTarget}
        title="Disable Account"
        message={`Disable ${disableTarget?.display_name}'s account? They will be unable to log in.`}
        confirmLabel="Disable"
        danger
        loading={updateUser.isPending}
        onConfirm={() => disableTarget && handleToggleActive(disableTarget, false)}
        onCancel={() => setDisableTarget(null)}
      />
    </div>
  );
}
